import { useEffect, useRef, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import LifeRail from '../components/LifeRail';
import Pagination, { usePagination } from '../components/Pagination';
import EnrolmentDrawer from '../components/EnrolmentDrawer';
import { withProductColumns, productDropdowns, columnHeadings, withHeadings, fromHeadings } from '../utils/productColumns';
import { resultRowEmail } from '../utils/format';
// utils/xlsx.js pulls in exceljs (a genuinely large library) purely to
// build/read the .xlsx templates below — dynamically imported so it's its
// own chunk, only fetched when a user actually downloads or uploads one,
// same reasoning as IntakeView's BulkUploadPanel.
const loadXlsxUtils = () => import('../utils/xlsx');

/* ICIB-04 — Automated workflows for onboarding and offboarding employees.
   Two lists, scoped to the currently-selected group (same scoping as the
   single-item endpoints themselves): enrolments sitting at `invited`
   (insurer's onboarding outcome still to be recorded) and at `removed`
   (insurer's removal still to be confirmed). Each list offers both the
   single-item action buttons and a bulk .xlsx upload (ICIB-04b/d) below
   the table — see BulkOutcomePanel; both forms hit different endpoints
   but land on the same two refetches (loadNotifications,
   loadGroupEnrolments) under the hood.

   Reads ICIB-12 (GET /companies/{gid}/enrolments) directly — a lean,
   group-wide enrolment book (enrolment_id, employee_name, product *name*,
   status; one row per enrolment) — filtered client-side into the two
   lists below, rather than the ICIB-01a employee summary + an ICIB-01b
   drill-down per employee this view used before ICIB-12 existed. ICIB-12
   doesn't carry the person's email or an invited_at/removed_at timestamp
   — selecting a row (same pattern as FollowupView) fetches the full
   record via GET /enrolments/{enrolment_id} (ICIB-03b) and opens a
   drawer with everything that endpoint returns. */
function ConfirmationsView() {
  const {
    status, groupId, curGroup, confirmOnboarding, confirmRemoval,
    applyBulkOnboarding, applyBulkConfirmRemoval,
    groupEnrolments, groupEnrolmentsStatus, groupEnrolmentsError, loadGroupEnrolments,
    loadEnrolmentDetail, product, group,
  } = useIcibAdmin();
  const { openDrawer } = useDrawer();
  const toast = useToast();
  const [busy, setBusy] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const selectRequestRef = useRef(0);
  const [qInvited, setQInvited] = useState('');
  const [qRemoved, setQRemoved] = useState('');

  useEffect(() => { loadGroupEnrolments(groupId); }, [groupId, loadGroupEnrolments]);

  // ICIB-12 rows carry no email — search what each row actually shows.
  const matches = (row, q) => {
    const needle = q.trim().toLowerCase();
    return !needle || `${row.employee_name || ''} ${row.product || ''}`.toLowerCase().includes(needle);
  };
  const invited = groupEnrolments.filter((en) => en.status === 'invited' && matches(en, qInvited));
  const removed = groupEnrolments.filter((en) => en.status === 'removed' && matches(en, qRemoved));
  const invitedPage = usePagination(invited);
  const removedPage = usePagination(removed);

  async function onSelect(row) {
    setSelectedId(row.enrolment_id);
    // Always refetch rather than reuse enrolmentDetails' cached copy — same
    // reason as FollowupView's onSelect: that cache is filled elsewhere
    // (Overview's prefetch, Follow-up, resolveEnrolment) and never
    // invalidated, so a row whose status has since moved on (e.g. invited ->
    // removed) would open a drawer still showing the old status.
    const requestId = ++selectRequestRef.current;
    const detail = await loadEnrolmentDetail(row.enrolment_id);
    // A later click superseded this one while it was in flight.
    if (selectRequestRef.current !== requestId) return;
    if (detail?.error) { toast(detail.message || 'Could not load this enrolment.', 'bad', detail.error); return; }
    openDrawer(
      row.employee_name || 'Enrolment detail',
      `ICIB-03b · GET /enrolments/${row.enrolment_id}`,
      <EnrolmentDrawer detail={detail} product={product} group={group} />
    );
  }

  async function onboard(row, outcome) {
    setBusy(row.enrolment_id);
    const res = await confirmOnboarding(row.enrolment_id, outcome);
    setBusy(null);
    if (res?.error) toast(res.message || 'Could not record this outcome.', 'bad', res.error);
    else toast(`Recorded as ${outcome.replace(/_/g, ' ')}.`, 'ok');
  }

  async function archive(row) {
    setBusy(row.enrolment_id);
    const res = await confirmRemoval(row.enrolment_id);
    setBusy(null);
    if (res?.error) toast(res.message || 'Could not confirm this removal.', 'bad', res.error);
    else toast('Confirmed archived.', 'ok');
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Insurer outcomes</div>
      <div className="page-h"><h2>Enrolment status</h2></div>
      <p className="page-sub">Where the insurer's answer gets recorded for {curGroup()?.name || 'this company'} — joining and leaving alike.</p>

      {groupEnrolmentsError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load this company's enrolments</b>{groupEnrolmentsError}</div>}

      {!groupId ? (
        <div className="card"><div className="empty"><b>Select a company</b>Choose a company from the scope selector above.</div></div>
      ) : groupEnrolmentsStatus === 'loading' || groupEnrolmentsStatus === 'idle' ? (
        <div className="card"><div className="empty"><b>Loading enrolments…</b></div></div>
      ) : (
        <>
          <div className="sec-h">
            <h3>Awaiting onboarding outcome</h3><span className="spacer" />
            <span className="ep">PATCH .../enrolments/{'{enrolment_id}'}/onboarding-status</span>
          </div>
          <div className="filterbar">
            <input type="search" placeholder="Search name or product…" value={qInvited} onChange={(e) => setQInvited(e.target.value)} />
          </div>
          <div className="card">
            {invited.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Person</th><th>Product</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {invitedPage.pageItems.map((row) => (
                      <tr key={row.enrolment_id} className="clickable" aria-selected={selectedId === row.enrolment_id} onClick={() => onSelect(row)}>
                        <td><span className="strong">{row.employee_name}</span></td>
                        <td>{row.product}</td>
                        <td><LifeRail status={row.status} withLabel /></td>
                        <td>
                          <div className="btn-row">
                            <button className="btn sm pri" disabled={busy === row.enrolment_id} onClick={(e) => { e.stopPropagation(); onboard(row, 'onboarded'); }}>Onboarded</button>
                            <button className="btn sm danger" disabled={busy === row.enrolment_id} onClick={(e) => { e.stopPropagation(); onboard(row, 'onboarding_unsuccessful'); }}>Unsuccessful</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : qInvited.trim() ? <div className="empty"><b>No matches</b>Try a different search.</div>
              : <div className="empty"><b>Nothing waiting</b>Every invited enrolment has an outcome recorded.</div>}
            <Pagination {...invitedPage} />
          </div>

          <BulkOutcomePanel
            key={`onboarding-${groupId}`}
            title="Bulk onboarding outcome"
            endpointLabel="POST .../employees/enrolments/onboarding-status/bulk-upload"
            columns={['email', 'subsidised_product_level', 'onboarding_status']}
            starterValues={{ onboarding_status: 'onboarded' }}
            statusColumn={{ header: 'onboarding_status', values: ['onboarded', 'onboarding_unsuccessful'] }}
            hint="Onboarding Status must be onboarded or onboarding_unsuccessful — both a real dropdown in the .xlsx. Each row is matched to the invited enrolment(s) by email + the product columns marked Yes (mark several Yes to record the same outcome for several of the employee's enrolments at once), same as the single-item action above."
            groupId={groupId}
            groupCode={curGroup()?.company_code}
            onUpload={applyBulkOnboarding}
          />

          <div className="sec-h">
            <h3>Awaiting removal confirmation</h3><span className="spacer" />
            <span className="ep">PATCH .../enrolments/{'{enrolment_id}'}/confirm-removal</span>
          </div>
          <div className="filterbar">
            <input type="search" placeholder="Search name or product…" value={qRemoved} onChange={(e) => setQRemoved(e.target.value)} />
          </div>
          <div className="card">
            {removed.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Person</th><th>Product</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {removedPage.pageItems.map((row) => (
                      <tr key={row.enrolment_id} className="clickable" aria-selected={selectedId === row.enrolment_id} onClick={() => onSelect(row)}>
                        <td><span className="strong">{row.employee_name}</span></td>
                        <td>{row.product}</td>
                        <td><LifeRail status={row.status} withLabel /></td>
                        <td><button className="btn sm pri" disabled={busy === row.enrolment_id} onClick={(e) => { e.stopPropagation(); archive(row); }}>Confirm archived</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : qRemoved.trim() ? <div className="empty"><b>No matches</b>Try a different search.</div>
              : <div className="empty"><b>Nothing waiting</b>Every removal has been confirmed against the insurer.</div>}
            <Pagination {...removedPage} />
          </div>

          <BulkOutcomePanel
            key={`removal-${groupId}`}
            title="Bulk confirm removal"
            endpointLabel="POST .../employees/enrolments/confirm-removal/bulk-upload"
            columns={['email', 'subsidised_product_level']}
            hint="No status column — this endpoint only confirms the removal. Each row is matched to the removed enrolment(s) by email + the product columns marked Yes (mark several Yes to confirm several of the employee's removed enrolments at once), same as the single-item action above."
            groupId={groupId}
            groupCode={curGroup()?.company_code}
            onUpload={applyBulkConfirmRemoval}
          />
        </>
      )}
    </>
  );
}

// ICIB-04b/d — bulk .xlsx counterparts of the single-item onboarding-status
// and confirm-removal actions above. Same shape for both: download a
// starter template, upload a completed one, one atomic call either way
// (all rows are identified by email + subsidised_product_level rather
// than an enrolment_id, so there's no need to resolve rows against the
// tables above first). `onUpload` is whichever of applyBulkOnboarding /
// applyBulkConfirmRemoval this instance wraps.
//
// Same template style as the bulk Addition/Removal sheets (IntakeView,
// NewStarterView, LeaversView — utils/productColumns.js): the
// subsidised_product_level slot becomes one column per attached product,
// headed by its name, with a Yes/No dropdown. On upload the Yes columns are
// folded back into the id array the endpoint expects (ICIB-174). A legacy
// subsidised_product_level column (names/ids separated by ';') still works.
// `statusColumn`, when given, gets its own fixed-values dropdown (see
// downloadXlsxTemplate's listDropdowns); `starterValues` pre-fills the
// starter row.
function BulkOutcomePanel({ title, endpointLabel, columns, starterValues, statusColumn, hint, groupId, groupCode, onUpload }) {
  const { gProducts, product } = useIcibAdmin();
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  // A filled-in template can run to hundreds of rows — page the preview
  // rather than rendering the whole file at once. Upload still sends
  // every row, not just the visible page.
  const rowsPage = usePagination(rows);
  const [busy, setBusy] = useState(false);
  const [buildingTemplate, setBuildingTemplate] = useState(false);
  const [result, setResult] = useState(null);
  // The per-row log is as long as the file — paged like the preview.
  const resultPage = usePagination(result?.results || []);

  const headers = rows.length ? Object.keys(rows[0]) : [];

  const groupProductList = groupId ? gProducts(groupId) : [];
  const products = groupProductList.map((gp) => ({ id: gp.product_id, name: product(gp.product_id)?.name || gp.product_id }));
  const norm = (v) => String(v ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const productByName = new Map(products.map((p) => [norm(p.name), p]));
  const isYes = (v) => ['yes', 'y', 'true', '1'].includes(norm(v));
  // EXTR-13 — headings and header colours follow ICIB's own sheet, same as
  // the ICIB-02 Addition/Removal templates; fromHeadings maps them back to
  // field_keys when a file is read.
  const headings = columnHeadings([], products);

  // Folds a row's Yes/No product columns (plus any legacy ';'-separated
  // subsidised_product_level cell) into the id array the endpoint expects.
  function toUploadRow(row) {
    const out = {};
    const ids = [];
    for (const [key, value] of Object.entries(row)) {
      const p = productByName.get(norm(key));
      if (p) { if (isYes(value)) ids.push(p.id); }
      else if (key === 'subsidised_product_level') {
        String(value ?? '').split(/;/).map((t) => t.trim()).filter(Boolean).forEach((t) => {
          ids.push(products.find((x) => x.id === t)?.id || productByName.get(norm(t))?.id || t);
        });
      } else out[key] = value;
    }
    return { ...out, subsidised_product_level: [...new Set(ids)] };
  }

  async function onDownloadTemplate() {
    setBuildingTemplate(true);
    try {
      const { downloadXlsxTemplate } = await loadXlsxUtils();
      const cols = withProductColumns(columns, products);
      const productNames = new Set(products.map((p) => p.name));
      // Product columns default to No, so only those switched to Yes count.
      const starterRow = cols.map((c) => (productNames.has(c) ? 'No' : starterValues?.[c] ?? ''));
      await downloadXlsxTemplate({
        filename: `${groupCode || 'company'}_${title.toLowerCase().replace(/\s+/g, '_')}_template.xlsx`,
        starterRow,
        icibStyle: true,
        ...withHeadings({ headers: cols, listDropdowns: [...productDropdowns(products), ...(statusColumn ? [statusColumn] : [])] }, headings),
      });
    } finally {
      setBuildingTemplate(false);
    }
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    rowsPage.setPage(1);
    loadXlsxUtils().then(({ parseXlsx }) => file.arrayBuffer().then(parseXlsx)).then((parsed) => setRows(fromHeadings(parsed, headings)));
  }

  async function onSubmit() {
    if (!rows.length) return;
    // Skip rows with no email (e.g. the untouched starter row); every
    // other row needs at least one product marked Yes.
    const filled = rows.filter((r) => String(r.email ?? '').trim());
    const uploadRows = filled.map(toUploadRow);
    const problems = uploadRows.flatMap((r) => (r.subsidised_product_level.length ? [] : [`${r.email}: mark at least one product Yes.`]));
    if (!uploadRows.length) { toast('No rows with an email to upload.', 'bad'); return; }
    if (problems.length) { toast(problems.join(' '), 'bad'); return; }
    setBusy(true);
    const res = await onUpload(uploadRows);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not process this file.', 'bad', res.error); return; }
    setResult({ ...res, sentRows: uploadRows });
    resultPage.setPage(1);
    if (res.committed) {
      toast(`${res.rows_processed} row(s) committed.`, 'ok');
      setFileName(''); setRows([]);
    } else {
      toast('Some rows failed — nothing was written. Fix and re-upload.', 'bad');
    }
  }

  return (
    <div className="card card-b">
      <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
        <h4>{title}</h4>
        <span className="spacer" />
        <span className="ep">{endpointLabel}</span>
      </div>

      <div className="btn-row" style={{ marginBottom: 6 }}>
        <button type="button" className="btn sm" onClick={onDownloadTemplate} disabled={buildingTemplate}>{buildingTemplate ? 'Building…' : 'Download .xlsx template'}</button>
      </div>
      <div className="hint" style={{ marginBottom: 12 }}>
        {hint} Each of this company's attached products gets its own column with a Yes/No dropdown — the
        app turns the Yes columns into the product ids the backend expects automatically on upload.
      </div>

      <div className="field">
        <label>Completed .xlsx</label>
        <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} />
      </div>

      {rows.length > 0 && (
        <>
          <div className="hint mono" style={{ marginTop: 10 }}>{fileName} — {rows.length} row(s)</div>
          <div className="tbl-wrap" style={{ marginTop: 6, marginBottom: 10 }}>
            <table>
              <thead><tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {rowsPage.pageItems.map((r, i) => (
                  <tr key={i}>{headers.map((h) => <td key={h} className="mono">{r[h]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginBottom: 10 }}><Pagination {...rowsPage} /></div>
          <div className="btn-row">
            <button type="button" className="btn pri sm" disabled={busy} onClick={onSubmit}>{busy ? 'Uploading…' : `Upload ${rows.length} row(s) as one batch`}</button>
          </div>
        </>
      )}

      {result && (
        <div className={'note' + (result.committed ? ' ok' : ' bad')} style={{ marginTop: 12 }}>
          <b>{result.committed ? 'Committed' : 'Rejected — nothing written'}</b>
          {resultPage.pageItems.map((r) => (
            <div key={r.row} className="mono" style={{ fontSize: 11.5 }}>
              row {r.row}{resultRowEmail(result, r) && ` ${resultRowEmail(result, r)}`}: {r.status}{r.message ? ` — ${r.message}` : ''}
            </div>
          ))}
          <Pagination {...resultPage} />
        </div>
      )}
    </div>
  );
}

export default ConfirmationsView;
